import cookie from 'cookie';
import queryString from 'query-string';
import { sanitizeKey, sanitizeValue } from 'lib/gptSanitizeString';

/**
 * List of keys to remap different keys for SourceOfTruth
 * @type {Array}
 */
export const keyValueMap = {
  uniqueid: 'detailid',
};

/**
 * List of keys that can be carried over from mdManager
 * @type {Array}
 */
export const validKeys = [
  'site',
  'partner',
  'keyword',
  'env_device_type',
  'ctx_template',
  'ctx_page_slug',
  'ctx_page_channel',
  'ctx_page_sub_channel',
  'cnt_tags',
  'cnt_copilotid',
  'series',
  'pos_instance',
  'pagetype',
  'categorydspname',
  'sctndspname',
  'detailid',
  'userid',
  'subscriptionid',
  'redesign',
  'includeexco',
];

function getContentType(template) {
  const contentTypeMap = {
    bundle: /^(index(_[\w_]+)?|subindex(_[\w_]+)?|bundle(_[\w_]+)?|hotlist(_[\w_]+)?|topic|videoHub(LandingPage|SeriesDetailPage|AllSeriesLandingPage)|courseListingPage|clubCollectionPage|equipmentPage|courseCollectionMapPage|clinicLandingPage)$/i,
    article: /^(article(_[\w_]+)?|desktop_article(_[\w_]+)?|hotlist|videoPlayerPage|twoMinuteClinicPage|swingSequencePage|stressTestPage)$/i,
    contributor: /^contributor$/i,
    search: /^search$/i,
    'site-info': /^error|sitemap|static$/i,
  };

  const match = Object.keys(contentTypeMap)
    .find(key => contentTypeMap[key] instanceof RegExp && contentTypeMap[key].exec(template));
  return match || template;
}

/**
 * List of multi-value keys and how to parse them
 * @type {Array}
 */
export const multiValueKeys = [
  {
    site: '*',
    key: 'cnt_tags',
    separator: ',',
    dfpKey: 'cnt_tags',
    clean: false,
  },
  {
    site: '*',
    key: 'subscriptionid',
    separator: ' ',
    dfpKey: 'subscriptionid',
    clean: true,
  },
];

export function getCookieValues() {
  const cookies = cookie.parse(window.document.cookie);
  const validCookies = {};

  if (cookies['AMCV_9AE0F0145936E3790A495CAA%40AdobeOrg']) {
    const adobeCookie = ['id', ...cookies['AMCV_9AE0F0145936E3790A495CAA%40AdobeOrg'].split('|')]
      .reduce((prev, current, index, array) =>
        (index % 2 === 0 ? { ...prev, [current]: array[index + 1] } : prev),
      {});

    validCookies.mcid = adobeCookie.MCMID;
  }

  validCookies.us_privacy = cookies.us_privacy;
  validCookies.usprivacy = cookies.usprivacy;

  return validCookies;
}

/**
 * Retrieves meta data from mdManager and returns it
 * @return {Object} Meta data from mdManager
 */
export default function golfCoreMetaData() {
  try {
    const { mdManager } = window;
    const { search } = window.location;
    const site = 'golfdigest';
    /* eslint-disable-next-line camelcase */
    const { ao_test } = queryString.parse(search);

    const matchingMultiValueKeys = multiValueKeys
      .filter(item => item.site === site || item.site === '*');

    const keyValues = {};

    validKeys.forEach((key) => {
      const sanitizedKey = sanitizeKey(key);

      const sanitizedValue = sanitizeValue(mdManager.getParameterString(key));
      keyValues[sanitizedKey] = sanitizedValue;

      // Multi value keys can replace existing raw KVP above, or write to a new key
      const matchingMultiValueKey = matchingMultiValueKeys.find(item => item.key === key);
      if (matchingMultiValueKey) {
        const rawValue = mdManager.getParameterString(key);
        const sanitizedMultiValue = rawValue
          .split(matchingMultiValueKey.separator)
          .map((v) => sanitizeValue(v));

        keyValues[matchingMultiValueKey.dfpKey || sanitizedKey] = sanitizedMultiValue;
      }

      Object.keys(keyValueMap).forEach((newKey) => {
        if (keyValues[keyValueMap[newKey]]) keyValues[newKey] = keyValues[keyValueMap[newKey]];
      });
    });

    const cookieValues = getCookieValues();

    /* eslint-disable camelcase */
    if (ao_test !== undefined) {
      keyValues.ao_test = ao_test;
    }
    /* eslint-enable camelcase */

    keyValues.contentType = getContentType(keyValues.ctx_template);

    // Some weird inconsistency with this value
    // if (keyValues.ctx_page_channel === 'homepage') keyValues.ctx_page_channel = 'home';

    // Some pages use TYPE instead of PAGETYPE in mdManager
    const pagetype = keyValues.pagetype || mdManager.getParameterString('TYPE');

    keyValues.config_channel = 'golf';

    return { ...keyValues, ...cookieValues, pagetype };
  } catch (e) {
    throw new Error(e);
  }
}
